/**
 *
 * Author: Devon
 *
 * Component: NavigateLink
 * Description:
 * - Component as a customizable link that integrates with React Router.
 * - modifying query parameters from the current URL while allowing the addition of new parameters.
 *
 * Hooks: UseCustomNavigate
 * Description:
 * - Provides a custom navigation function tailored to retain and modify query parameters.
 */

import type { SystemStyleObject } from '@chakra-ui/react';
import { Link } from '@chakra-ui/react';
import type { AnchorHTMLAttributes, PropsWithChildren } from 'react';
import { useCallback, useMemo } from 'react';
import type { NavigateOptions } from 'react-router-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { URL_PREFIX } from 'watchtower-ui/utils/Constants';
import {
  existingURLPath,
  filterQueryParams,
  isValidInternalPath,
  sanitizeSearchParams,
} from 'watchtower-ui/utils/utilityFunctions';

type TPathOptions = {
  removePrefix?: boolean;
  removeCountry?: boolean;
};

type TSearchParamProps = {
  searchParam?: string;
  extendSearchParam?: boolean;
  keptQueryParams?: readonly string[];
};

const getSearchParams = (search: string, { searchParam, extendSearchParam, keptQueryParams }: TSearchParamProps) => {
  let params = '';

  const existingParams = filterQueryParams(search, keptQueryParams ?? []);

  if (extendSearchParam) {
    params += existingParams;
  }

  if (searchParam) {
    params += `&${searchParam}`;
  }

  return params;
};

type TNavigateLinkProps = {
  hover?: SystemStyleObject;
  to: string;
  color?: string;
  sx?: SystemStyleObject;
  hash?: string;
} & TPathOptions &
  TSearchParamProps &
  AnchorHTMLAttributes<HTMLAnchorElement>;

export const NavigateLink = ({
  hover = {},
  to,
  color,
  searchParam,
  extendSearchParam = false,
  keptQueryParams = [],
  sx = {},
  hash,
  removePrefix = false,
  removeCountry = false,
  children,
  ...rest
}: PropsWithChildren<TNavigateLinkProps>) => {
  const { search } = useLocation();

  const searchParams = useMemo(
    () => getSearchParams(search, { searchParam, extendSearchParam, keptQueryParams }),
    [search, searchParam, extendSearchParam, keptQueryParams],
  );

  const pathname = useMemo(() => {
    let path = to.trim();

    if (!removeCountry) {
      const countryCode = existingURLPath('country') || '';
      path = `${countryCode}${path}`;
    }

    if (!removePrefix) {
      path = `${URL_PREFIX}${path}`;
    }

    return path;
  }, [to, removePrefix, removeCountry]);

  return (
    <Link
      _hover={hover ?? {}}
      as={NavLink}
      sx={sx ?? {}}
      to={{
        pathname,
        hash,
        search: searchParams,
      }}
      color={color ?? 'textBlack'}
      {...rest}
    >
      {children}
    </Link>
  );
};

type TCustomNavigateProps = {
  to: string;
  options?: Omit<NavigateOptions, 'state'>;
} & TPathOptions &
  TSearchParamProps;

export const useCustomNavigate = () => {
  const { search } = useLocation();
  const navigate = useNavigate();

  return useCallback(
    (nav: string | TCustomNavigateProps) => {
      const countryCode = existingURLPath('country') || '';

      if (typeof nav === 'string') {
        const trimmedPath = nav.trim();
        if (!isValidInternalPath(trimmedPath)) {
          console.warn('Invalid navigation path detected:', trimmedPath);
          return;
        }
        const safePath = `${URL_PREFIX}${countryCode}${trimmedPath}`;
        navigate(safePath);
        return;
      }

      const { to, options, removePrefix, removeCountry, ...searchParams } = nav;

      let path = to.trim();

      if (!isValidInternalPath(path)) {
        console.warn('Invalid navigation path detected:', path);
        return;
      }

      if (!removeCountry) {
        path = `${countryCode}${path}`;
      }

      if (!removePrefix) {
        path = `${URL_PREFIX}${path}`;
      }

      const searchParamsString = getSearchParams(sanitizeSearchParams(search), searchParams);

      navigate({
        ...options,
        pathname: path,
        search: searchParamsString,
      });
    },
    [search, navigate],
  );
};
